import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { InputCatalogue } from "../../../../MainComponents/Catalogues/InputCatalogue";
import { getSelectionChain } from "../../../../MainComponents/Catalogues/utils/GetSelectionChain";
import { InvestigationButton, Separator } from "../components";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import {
  CASE_TYPE_CATALOGUE,
  CASE_TYPE_IS_OWN,
  CASE_TYPE_SEPARATOR,
  caseTypeLabel,
  FACTS_VERIFIED_VALUES,
  GRC_LEVELS,
  SEVERITY_LEVELS,
  emptyCaseType,
  isCaseClassificationEmpty,
  normalizeCaseClassification,
  parseSeverity,
  serializeCaseClassification,
} from "../../../../../helpers/caseClassification";

// La cadena del catalogo se arma con este separador; en pantalla solo se
// muestra el ultimo nivel (ver caseTypeLabel).
const CATALOGUE_SEPARATOR = CASE_TYPE_SEPARATOR;

/**
 * Clasificacion del caso, dentro de la carpeta de investigacion. Se ve como
 * ficha y se edita en el mismo lugar, igual que el responsable asignado.
 */
export const CaseClassification = ({
  investigation,
  onSave,
  justView = false,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const saved = useMemo(
    () => normalizeCaseClassification(investigation?.case_classification),
    [investigation?.case_classification]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(saved);

  useEffect(() => {
    setForm(saved);
  }, [saved]);

  const handleChange = (field) => (value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // --- Clasificaciones (varias) ---
  // En el formulario siempre hay al menos un renglón, aunque esté vacío: si no,
  // no habría dónde elegir la primera.
  const caseTypeRows = form.case_types.length
    ? form.case_types
    : [emptyCaseType()];

  const setCaseTypes = (rows) =>
    setForm((prev) => ({ ...prev, case_types: rows }));

  const handleCaseTypeChange = (index) => (path) =>
    setCaseTypes(
      caseTypeRows.map((row, i) =>
        // La etiqueta se resuelve al guardar; aquí solo se conoce el path.
        i === index ? { path: path ?? "", label: "" } : row
      )
    );

  const handleAddCaseType = () =>
    setCaseTypes([...caseTypeRows, emptyCaseType()]);

  const handleRemoveCaseType = (index) =>
    setCaseTypes(caseTypeRows.filter((_, i) => i !== index));

  // Una clasificación repetida no aporta y confunde el expediente.
  const duplicatedCaseType = caseTypeRows.some(
    (row, index) =>
      row.path && caseTypeRows.findIndex((r) => r.path === row.path) !== index
  );

  const handleGrcChange = (index) => (value) =>
    setForm((prev) => ({
      ...prev,
      grc: prev.grc.map((level, i) => (i === index ? value : level)),
    }));

  const factsVerifiedOptions = useMemo(
    () =>
      FACTS_VERIFIED_VALUES.map((value) => ({
        value,
        label: t(`caseClassification.facts_${value}`),
      })),
    [t]
  );

  const severityOptions = useMemo(
    () =>
      SEVERITY_LEVELS.map((level) => ({
        value: level,
        label: `${level} - ${t(`caseClassification.severity_${level}`)}`,
      })),
    [t]
  );

  // El tipo de caso se guarda con su etiqueta ya resuelta: asi la ficha no
  // tiene que volver a consultar el catalogo cada vez que se pinta, y el
  // expediente conserva como se llamaba al momento de clasificarlo.
  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Las etiquetas de todas las clasificaciones se resuelven en paralelo.
      const caseTypes = await Promise.all(
        caseTypeRows
          .filter((row) => row.path)
          .map(async (row) => ({
            path: row.path,
            label:
              (await getSelectionChain({
                catalogue: CASE_TYPE_CATALOGUE,
                isOwn: CASE_TYPE_IS_OWN,
                value: row.path,
                separator: CATALOGUE_SEPARATOR,
              })) || row.label,
          }))
      );

      await onSave(
        serializeCaseClassification({ ...form, case_types: caseTypes })
      );
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar({
        variant: "error",
        message: t("caseClassification.save_error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setForm(saved);
    setIsEditing(false);
  };

  const third = { xs: 12, md: 4 };

  const renderView = () => {
    if (isCaseClassificationEmpty(saved)) {
      return (
        <Typography fontSize={14} color={theme.palette.blueGray500.main} mt={1}>
          {t("caseClassification.empty")}
        </Typography>
      );
    }

    return (
      <Grid2 container spacing={2} mt={1}>
        {/* Las clasificaciones arriba y a todo lo ancho: son la cabeza de la
            ficha y sus rutas de catálogo son largas. */}
        <Grid2 size={12}>
          <Typography
            fontSize={13}
            fontWeight={600}
            color={theme.palette.secondary.main}
          >
            {t("caseClassification.case_types", {
              count: saved.case_types.length,
            })}
          </Typography>
          {saved.case_types.length ? (
            <Stack component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, gap: 0.25 }}>
              {saved.case_types.map((caseType) => (
                <Typography
                  component="li"
                  key={caseType.path}
                  fontSize={14}
                  color={theme.palette.oxfordBlue.main}
                >
                  {caseTypeLabel(caseType)}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography fontSize={14} color={theme.palette.blueGray500.main}>
              —
            </Typography>
          )}
        </Grid2>

        <InvestigationBlockInfo
          size={third}
          label={t("caseClassification.facts_verified")}
          text={
            saved.facts_verified
              ? t(`caseClassification.facts_${saved.facts_verified}`)
              : ""
          }
        />
        <InvestigationBlockInfo
          size={third}
          label={t("caseClassification.severity")}
          text={
            saved.severity === null
              ? ""
              : `${saved.severity} - ${t(
                  `caseClassification.severity_${saved.severity}`
                )}`
          }
        />
        <InvestigationBlockInfo
          size={third}
          label={t("caseClassification.discipline_case_number")}
          text={saved.discipline_case_number}
        />
        <InvestigationBlockInfo
          size={12}
          label={t("caseClassification.grc")}
          text={saved.grc.filter(Boolean).join(CATALOGUE_SEPARATOR)}
        />
        <InvestigationBlockInfo
          size={12}
          label={t("caseClassification.accusation")}
          text={saved.accusation}
        />
      </Grid2>
    );
  };

  const renderForm = () => (
    <Grid2 container spacing={2} mt={1}>
      {/* Clasificaciones: una por renglón, a todo lo ancho. Cada InputCatalogue
          encadena solo los niveles que traiga RC-100 y puede crecer bastante,
          así que compartir fila con otros campos los dejaba apretados. */}
      <Grid2 size={12}>
        <Typography
          fontSize={13}
          fontWeight={600}
          color={theme.palette.secondary.main}
        >
          {t("caseClassification.case_types", { count: caseTypeRows.length })}
        </Typography>
        <Typography fontSize={12} color={theme.palette.blueGray500.main} mb={1}>
          {t("caseClassification.case_types_hint")}
        </Typography>

        <Stack spacing={1}>
          {caseTypeRows.map((row, index) => (
            <Stack
              key={`case-type-${index}`}
              direction="row"
              alignItems="flex-start"
              spacing={1}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <InputCatalogue
                  label={t("caseClassification.case_type_number", {
                    number: index + 1,
                  })}
                  catalogue={CASE_TYPE_CATALOGUE}
                  isOwn={CASE_TYPE_IS_OWN}
                  defaultValue={row.path}
                  onChange={handleCaseTypeChange(index)}
                  wrapper={<Box marginBottom={1} />}
                />
              </Box>
              {/* La primera solo se puede quitar si hay más de una: siempre
                  debe quedar un renglón donde elegir. */}
              {caseTypeRows.length > 1 && (
                <EGIconButton
                  title={t("caseClassification.case_type_remove")}
                  iconName="trash_can"
                  iconColor="error"
                  iconSize={18}
                  onClick={() => handleRemoveCaseType(index)}
                />
              )}
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} mt={1}>
          <InvestigationButton
            label={t("caseClassification.case_type_add")}
            icon="add_circle"
            size="medium"
            width="auto"
            onClick={handleAddCaseType}
          />
          {duplicatedCaseType && (
            <Typography fontSize={12} color={theme.palette.error.main}>
              {t("caseClassification.case_type_duplicated")}
            </Typography>
          )}
        </Stack>
      </Grid2>

      <Grid2 size={third}>
        <EGBasicSelect
          label={t("caseClassification.facts_verified")}
          value={form.facts_verified}
          options={factsVerifiedOptions}
          onChange={(e) => handleChange("facts_verified")(e.target.value)}
          isValid={null}
          showHelpText={false}
        />
      </Grid2>
      <Grid2 size={third}>
        <EGBasicSelect
          label={t("caseClassification.severity")}
          value={form.severity ?? ""}
          options={severityOptions}
          onChange={(e) =>
            handleChange("severity")(parseSeverity(e.target.value))
          }
          isValid={null}
          showHelpText={false}
        />
      </Grid2>
      <Grid2 size={third}>
        <EGInput
          label={t("caseClassification.discipline_case_number")}
          value={form.discipline_case_number}
          onChange={(e) =>
            handleChange("discipline_case_number")(e.target.value)
          }
          showHelpText={false}
        />
      </Grid2>

      <Grid2 size={12}>
        <Typography
          fontSize={13}
          fontWeight={600}
          color={theme.palette.secondary.main}
        >
          {t("caseClassification.grc")}
        </Typography>
        <Typography fontSize={12} color={theme.palette.blueGray500.main} mb={1}>
          {t("caseClassification.grc_hint")}
        </Typography>
        <Grid2 container spacing={2}>
          {Array.from({ length: GRC_LEVELS }, (_, index) => (
            <Grid2 size={{ xs: 12, sm: 6, lg: 3 }} key={`grc-${index}`}>
              <EGInput
                label={t("caseClassification.grc_level", { level: index + 1 })}
                value={form.grc[index]}
                onChange={(e) => handleGrcChange(index)(e.target.value)}
                showHelpText={false}
              />
            </Grid2>
          ))}
        </Grid2>
      </Grid2>

      <Grid2 size={12}>
        <EGInput
          label={t("caseClassification.accusation")}
          value={form.accusation}
          onChange={(e) => handleChange("accusation")(e.target.value)}
          multiline
          rows={4}
          showHelpText={false}
        />
      </Grid2>

      <Grid2 size={12}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          spacing={1}
        >
          <InvestigationButton
            label={t("caseClassification.save")}
            variant="success"
            size="medium"
            icon="mark_check_bold"
            onClick={handleSave}
            loading={isLoading}
          />
          <InvestigationButton
            label={t("caseClassification.cancel")}
            icon="close_icon"
            size="medium"
            onClick={handleCancel}
            loading={isLoading}
          />
        </Stack>
      </Grid2>
    </Grid2>
  );

  return (
    <Box
      className="rounded dyTheme2 pt-[10] pe-[20] pb-[16] ps-[20]"
      mt={1.7}
      marginBottom={2}
      sx={{ border: `1px solid ${theme.palette.primary.main}` }}
    >
      <Separator
        text={t("caseClassification.title")}
        icon="outline_clipboard_text"
        iconButton={!justView && !isEditing ? "outline_edit" : undefined}
        onClick={!justView && !isEditing ? () => setIsEditing(true) : undefined}
      />

      {isEditing ? renderForm() : renderView()}
    </Box>
  );
};
